import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Work as WorkIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchDesignations,
  toggleDesignationStatus,
} from "store/features/company/userManagement/designation/designationActions";
import {
  selectDesignations,
  selectDesignationLoading,
} from "store/features/company/userManagement/designation/designationSelectors";
import AddDesignationModal from "./AddDesignationModal";
import EditDesignationModal from "./EditDesignationModal";
import designationsSvg from "assets/folders/designations.svg";
import usePermissions from "hooks/usePermissions";

const Designation = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const designations = useSelector(selectDesignations);
  const loading = useSelector(selectDesignationLoading);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [designationToDelete, setDesignationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { hasPermission, hasAnyPermission } = usePermissions();

  const handleStatusToggle = async (designation) => {
    await dispatch(toggleDesignationStatus(designation.id, designation.status));
    dispatch(fetchDesignations());
  };

  useEffect(() => {
    dispatch(fetchDesignations());
  }, [dispatch]);

  const filteredDesignations = designations.filter((designation) => {
    const matchesSearch =
      searchTerm === "" ||
      designation.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      designation.department_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || designation.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDeleteClick = (designation) => {
    setDesignationToDelete(designation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDesignationToDelete(null);
  };

  const formatSalary = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const exportDesignationsToExcel = () => {
    if (!filteredDesignations || filteredDesignations.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["DESIGNATIONS REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 8 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Designations", filteredDesignations.length],
        [
          "Active Designations",
          filteredDesignations.filter((d) => d.status === "active").length,
        ],
        [
          "Inactive Designations",
          filteredDesignations.filter((d) => d.status === "inactive").length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 6;

    // Headers
    const headers = [
      "ID",
      "Code",
      "Designation Name",
      "Description",
      "Department",
      "Level",
      "Salary Range Min",
      "Salary Range Max",
      "Status",
      "Created Date",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredDesignations.forEach((designation) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            designation.id || "",
            designation.code || "",
            designation.name || "",
            designation.description || "",
            designation.department_name || "N/A",
            designation.level || "",
            designation.salary_range_min || "",
            designation.salary_range_max || "",
            designation.status || "",
            designation.created_at
              ? new Date(designation.created_at).toLocaleString()
              : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = Array(headers.length).fill({ wch: 20 });

    XLSX.utils.book_append_sheet(wb, ws, "Designations");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Designations_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Active"
          size="small"
          sx={{
            justifyContent: "flex-start",
            width: 90,
            px: 0.5,
            backgroundColor: "#d1fae5",
            color: "#0abe31",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "30px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(41, 185, 16, 0.25)",
              color: "#6ee774",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14 }} />}
        label="Inactive"
        size="small"
        sx={{
          justifyContent: "flex-start",
          width: 90,
          px: 0.5,
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "30px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(239,68,68,0.25)",
            color: "#fca5a5",
          }),
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={designationsSvg}
              alt="Designations"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Designations
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage job titles and position levels
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportDesignationsToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("designation.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Designation
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by name, code or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading designations...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Code
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Designation
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Level
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Salary Range
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Created At
                  </TableCell>
                  {hasAnyPermission([
                    "designation.update",
                    "designation.delete",
                  ]) && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDesignations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <WorkIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No designations found. Click "Add Designation" to get
                        started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDesignations
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((designation, index) => (
                      <TableRow
                        key={designation.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.5)"
                                : "#f9fafb",
                          },
                        }}
                      >
                        <TableCell>
                          <Stack>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#bbbfc8"
                                    : "#53555a",
                              }}
                            >
                              #{designation.id}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                              }}
                            >
                              {/* <b>Tenant: </b> {designation.tenant_id} */}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <WorkIcon
                              sx={{ fontSize: 16, color: "primary.main" }}
                            />
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                color: "text.primary",
                              }}
                            >
                              {designation.code}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {designation.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "text.secondary",
                              }}
                            >
                              {designation.description || "No description"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={designation.department_name || "N/A"}
                            size="small"
                            sx={{
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              ...(theme.palette.mode === "dark" && {
                                backgroundColor: "rgba(59,130,246,0.25)",
                                color: "#93c5fd",
                              }),
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            Level {designation.level}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                            }}
                          >
                            {formatSalary(designation.salary_range_min)} -{" "}
                            {formatSalary(designation.salary_range_max)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {hasPermission("designation.update") ? (
                            <Box
                              onClick={() => handleStatusToggle(designation)}
                              sx={{
                                display: "inline-flex",
                                backgroundColor: "transparent",
                                border: `1px solid ${
                                  theme.palette.mode === "dark"
                                    ? "#475569"
                                    : "#d1d5db"
                                }`,
                                borderRadius: "40px",
                                padding: "2px",
                                cursor: "pointer",
                                width: "fit-content",
                                minWidth: "140px",
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "40px",
                                  backgroundColor:
                                    designation.status === "active"
                                      ? theme.palette.mode === "dark"
                                        ? "#10b981"
                                        : "#d1fae5"
                                      : "transparent",
                                  color:
                                    designation.status === "active"
                                      ? theme.palette.mode === "dark"
                                        ? "#ffffff"
                                        : "#065f46"
                                      : theme.palette.mode === "dark"
                                      ? "#9ca3af"
                                      : "#6b7280",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                                Active
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "40px",
                                  backgroundColor:
                                    designation.status === "inactive"
                                      ? theme.palette.mode === "dark"
                                        ? "#ef4444"
                                        : "#fee2e2"
                                      : "transparent",
                                  color:
                                    designation.status === "inactive"
                                      ? theme.palette.mode === "dark"
                                        ? "#ffffff"
                                        : "#991b1b"
                                      : theme.palette.mode === "dark"
                                      ? "#9ca3af"
                                      : "#6b7280",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CancelIcon sx={{ fontSize: 14 }} />
                                Inactive
                              </Box>
                            </Box>
                          ) : (
                            <StatusBadge status={designation.status} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <EventIcon
                                sx={{ fontSize: 14, color: "primary.main" }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#d1d5db"
                                      : "#4b5563",
                                }}
                              >
                                {formatDateTime(designation.created_at)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            {hasPermission("designation.update") && (
                              <IconButton
                                onClick={() => {
                                  setSelectedDesignation(designation);
                                  setShowEditModal(true);
                                }}
                                size="small"
                                sx={{
                                  color: "primary.main",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(245,158,11,0.2)"
                                        : "#f6e5a1",
                                  },
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && filteredDesignations.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredDesignations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
              {
                color: "text.secondary",
              },
          }}
        />
      )}

      {/* Empty State for No Search Results */}
      {!loading &&
        filteredDesignations.length === 0 &&
        designations.length > 0 && (
          <Paper
            sx={{
              mt: 4,
              p: 4,
              textAlign: "center",
              borderRadius: "16px",
              bgcolor: "background.paper",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
            <Typography
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              No designations match your search criteria
            </Typography>
            <Button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
                setPage(0);
              }}
              sx={{
                color: "primary.main",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }}
            >
              Clear filters
            </Button>
          </Paper>
        )}

      {/* Modals */}
      <AddDesignationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchDesignations())}
      />

      <EditDesignationModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDesignation(null);
        }}
        designation={selectedDesignation}
        onSuccess={() => dispatch(fetchDesignations())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            <IconButton
              onClick={handleDeleteCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              component="img"
              src="/src/assets/modals/delete-modal.svg"
              alt="Inactivate"
              sx={{
                width: 80,
                height: 80,
                objectFit: "contain",
                margin: "0 auto",
                display: "block",
                mb: 2,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Inactivate Designation
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to inactivate
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{designationToDelete?.name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This designation will become inactive and won't be available for
              future assignments.
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default Designation;
